import React, { useEffect, useRef } from "react";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import {
  DEFAULT_ORDER_PICKUP_TIME,
  FULFILLMENT_METHOD_TYPES,
  ORDER_STATUS_TYPES,
  VERTICALS
} from "../../utils/constants";
import { Formik } from "formik";
import { add } from "date-fns";
import { yyyyMMdd } from "../../utils/helper";
import HomeCareOrderCreateWrapper from "./homecare-order-create-wrapper";

const HOMECARE_PRESCRIPTION_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const homeCarePrescriptionFileOk = file => {
  if (!file || !(file instanceof File)) return { ok: true };
  const maxBytes = 10 * 1024 * 1024;
  if (file.size > maxBytes) {
    return { ok: false, message: "Prescription must be at most 10 MB." };
  }
  const nameOk = /\.(pdf|jpe?g|png)$/i.test(file.name);
  const typeOk = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
    ""
  ].includes(file.type);
  if (nameOk || typeOk) return { ok: true };
  return {
    ok: false,
    message: "Prescription must be a PDF, JPEG, or PNG file."
  };
};

const HomeCareOrderCreate = props => {
  const {
    match: {
      params: { id }
    },
    customerIds,
    customersLoading,
    getCustomersId,
    getCustomerDetail,
    customerDetail,
    history,
    resetCustomerState,
    scanTypes,
    getOrderScanTypes,
    scanTypesLoading,
    resetScanTypesState,
    cities,
    createCustomerAddress,
    createCustomerPatient,
    updateCustomerPatient,
    deleteCustomerPatient,
    createCustomer,
    createOrder,
    ordersLoading,
    HomeCareOrderParams,
    deleteCustomerAddress,
    hearAboutUsList,
    getHearAboutUsList,
    getOrderTimeSlots,
    resetOrderTimeSlotsState,
    orderTimeSlots,
    resetCopyOrder,
    copiedOrder,
    getCities,
    regions,
    getOrderSources,
    orderSources,
    homeCareCategories,
    getHomeCareCategories,
    getPartialOrderDetail,
    orders: order
  } = props;

  useEffect(() => {
    getCities({ vertical: VERTICALS.homecare });
  }, []);

  useEffect(() => {
    if (id) getPartialOrderDetail({ id });
  }, [id]);

  /** Holds the latest file so submit always sees it (Formik can submit before setFieldValue flushes). */
  const homeCarePrescriptionFileRef = useRef(null);

  const onDiscard = e => {
    e.preventDefault();
    homeCarePrescriptionFileRef.current = null;
    resetScanTypesState();
    resetOrderTimeSlotsState();
    resetCopyOrder();
    history.goBack();
  };

  const orderInitialValues = {
    patient: "",
    value: "",
    discount: "",
    additionalNotes: "",
    pickupDate: add(new Date(), {
      minutes: DEFAULT_ORDER_PICKUP_TIME
    }).toString(),
    pickup: "",
    timeSlot: "",
    scanType: [],
    urgentDelivery: false,
    requiresFemaleStaff: false,
    customerAddress: "",
    city: "",
    hearAbout: null,
    fulfillmentMethod: FULFILLMENT_METHOD_TYPES.ONLINE,
    orderSource: null,
    homeCareCategory: null,
    orderStatus: ORDER_STATUS_TYPES.BOOKED,
    prescriptionFile: null,
    estimatedTime: "",
    hasAllergy: false,
    allergies: ""
  };

  return (
    <div id="wrapper">
      <Formik
        initialValues={orderInitialValues}
        validationSchema={Yup.object().shape({
          fulfillmentMethod: Yup.number().required("Please select order type"),
          patient: Yup.number().required(),
          discount: Yup.number()
            .min(0, "Order discount can not be less than 0")
            .max(100, "Order discount be less than or equal to 100")
            .typeError("Invalid order discount"),
          value: Yup.number()
            .min(0, "Order value can not be less than 0")
            .typeError("Invalid order value"),
          pickup: Yup.date().when("orderStatus", {
            is: val => val !== ORDER_STATUS_TYPES.BOOKED,
            then: Yup.date().required(),
            otherwise: Yup.date().nullable()
          }),
          timeSlot: Yup.number().when("orderStatus", {
            is: val => val !== ORDER_STATUS_TYPES.BOOKED,
            then: Yup.number().required(),
            otherwise: Yup.number().nullable()
          }),
          additionalNotes: Yup.string().max(
            1000,
            "Additional notes must be at most 1000 characters"
          ),
          scanType: Yup.array()
            .of(Yup.number())
            .min(1, "Select Test Type")
            .required(),
          orderSource: Yup.number().required("Please select order source"),
          homeCareCategory: Yup.number().required("Please select home care category"),
          prescriptionFile: Yup.mixed()
            .nullable()
            .test("prescription-type", "", function (value) {
              const file =
                homeCarePrescriptionFileRef.current instanceof File
                  ? homeCarePrescriptionFileRef.current
                  : value;
              const r = homeCarePrescriptionFileOk(file);
              if (r.ok) return true;
              return this.createError({ message: r.message });
            })
        })}
        onSubmit={values => {
          const data = { ...values };
          const prescriptionFile =
            homeCarePrescriptionFileRef.current instanceof File
              ? homeCarePrescriptionFileRef.current
              : data.prescriptionFile instanceof File
                ? data.prescriptionFile
                : null;
          delete data.prescriptionFile;
          homeCarePrescriptionFileRef.current = null;
          if (!(data.value > 0)) {
            delete data.value;
          }
          const isBooking = data.orderStatus === ORDER_STATUS_TYPES.BOOKED;
          const pickupDate = data.pickup;
          delete data["pickup"];
          delete data["pickupDate"];
          delete data["discount"];
          delete data["city"];
          if (!isEmpty(copiedOrder)) {
            data["sourceOrder"] = copiedOrder.sourceOrder;
          }
          const payload = {
            ...data,
            orderType: 1,
            customer: customerDetail.id,
            vertical: VERTICALS.homecare,
            isExternal: id ? true : false,
            partialOrderId: id ? order.id : null,
            // Explicitly set orderStatus - use provided value or default to BOOKED
            orderStatus: data.orderStatus !== undefined && data.orderStatus !== null && data.orderStatus !== "" 
              ? data.orderStatus 
              : ORDER_STATUS_TYPES.BOOKED
          };
          if (!isBooking && pickupDate) {
            payload.date = yyyyMMdd(pickupDate);
          }
          if (prescriptionFile) {
            payload.homeCarePrescriptionFile = prescriptionFile;
          }
          createOrder({
            orderParams: HomeCareOrderParams,
            data: payload
          });
        }}
      >
        {orderFormik => (
          <HomeCareOrderCreateWrapper
            orderFormik={orderFormik}
            customerIds={customerIds}
            customersLoading={customersLoading}
            getCustomersId={getCustomersId}
            getCustomerDetail={getCustomerDetail}
            customerDetail={customerDetail}
            history={history}
            resetCustomerState={resetCustomerState}
            createCustomerPatient={createCustomerPatient}
            updateCustomerPatient={updateCustomerPatient}
            deleteCustomerPatient={deleteCustomerPatient}
            onDiscard={onDiscard}
            createCustomer={createCustomer}
            ordersLoading={ordersLoading}
            orderInitialValues={orderInitialValues}
            copiedOrder={copiedOrder}
            createCustomerAddress={createCustomerAddress}
            cities={cities}
            deleteCustomerAddress={deleteCustomerAddress}
            scanTypes={scanTypes}
            getOrderScanTypes={getOrderScanTypes}
            scanTypesLoading={scanTypesLoading}
            resetScanTypesState={resetScanTypesState}
            hearAboutUsList={hearAboutUsList}
            getHearAboutUsList={getHearAboutUsList}
            getOrderTimeSlots={getOrderTimeSlots}
            resetOrderTimeSlotsState={resetOrderTimeSlotsState}
            orderTimeSlots={orderTimeSlots}
            regions={regions}
            getOrderSources={getOrderSources}
            orderSources={orderSources}
            homeCareCategories={homeCareCategories}
            getHomeCareCategories={getHomeCareCategories}
            order={order}
            id={id}
            prescriptionAccept={HOMECARE_PRESCRIPTION_ACCEPT}
            prescriptionFileValidate={homeCarePrescriptionFileOk}
            homeCarePrescriptionFileRef={homeCarePrescriptionFileRef}
          ></HomeCareOrderCreateWrapper>
        )}
      </Formik>
    </div>
  );
};
export default HomeCareOrderCreate;
